/**
 * A stored reference to a tested asset inside authored text (D2, S3.1).
 *
 * Question and answer text names an asset with a token that carries the
 * asset's `stableKey`, its identity on every wire, which a rename never
 * changes: `⟦asset:kit_plus⟧`. The text stores the identity; the name is the
 * asset's own. The model stages read and write the token itself (each asset
 * is shown to them with its token); the compiled survey renders it as the
 * asset's respondent label; the builder's HTTP envelope and the launch
 * snapshot render it as the `@Name` mention those readers have always read.
 *
 * The token is a literal protocol sentinel: it is parsed by its delimiters
 * alone, never by reading the words around it.
 */

const OPEN = "⟦asset:";
const CLOSE = "⟧";
const TOKEN = /⟦asset:([^⟧\s]+)⟧/g;

/** The stored token for the asset with `stableKey`. */
export function assetRefToken(stableKey: string): string {
  return `${OPEN}${stableKey}${CLOSE}`;
}

/** Whether `text` holds at least one asset token. */
export function hasAssetRefTokens(text: unknown): text is string {
  return typeof text === "string" && text.includes(OPEN);
}

/** The stable keys `text`'s tokens name, in order, repeats kept. */
export function assetRefKeysIn(text: unknown): Array<string> {
  if (!hasAssetRefTokens(text)) return [];
  return [...text.matchAll(TOKEN)].map((match) => match[1]);
}

/**
 * What is wrong with how one stored text names tested assets, read by the
 * token grammar alone (never by the words): a token whose key is not in
 * `keys`, and an `@` mention written outside a token (an `@` that starts a
 * word, as the builder's mention field starts one; an e-mail's `@` follows a
 * word character and is no mention). Every writer stores a mention as its
 * token: a model writes it, and the Save binds the builder's typed `@Name`
 * before this reads the text. Python's copy is
 * `models.questionnaire.asset_ref_tokens.asset_mention_issues`; one fixture
 * (`tests/fixtures/questionnaire-builder/asset-mention-issues.cases.json`)
 * holds both.
 */
export type AssetMentionIssue =
  | { code: "asset_mention_key_unknown"; token: string }
  | { code: "asset_mention_not_token" };

const MENTION_START = /(?:^|[^@A-Za-z0-9_])@/;

export function assetMentionIssuesIn(
  text: unknown,
  keys: ReadonlySet<string>,
): Array<AssetMentionIssue> {
  if (typeof text !== "string" || text === "") return [];
  const issues: Array<AssetMentionIssue> = assetRefKeysIn(text)
    .filter((key) => !keys.has(key))
    .map((key) => ({ code: "asset_mention_key_unknown", token: assetRefToken(key) }));
  if (MENTION_START.test(replaceAssetRefTokens(text, () => " "))) {
    issues.push({ code: "asset_mention_not_token" });
  }
  return issues;
}

/**
 * `text` with every token replaced by `render(stableKey)`. A token whose key
 * `render` does not know (undefined) is left as it is.
 */
export function replaceAssetRefTokens(
  text: string,
  render: (stableKey: string) => string | undefined,
): string {
  if (!hasAssetRefTokens(text)) return text;
  return text.replace(TOKEN, (token, key: string) => render(key) ?? token);
}

/**
 * `text` cut at its tokens, in order: the words between them as strings and
 * each token as the key it names. Text with no token is one piece.
 */
export function splitAssetRefTokens(
  text: string,
): Array<string | { stableKey: string }> {
  if (!hasAssetRefTokens(text)) return text ? [text] : [];
  const pieces: Array<string | { stableKey: string }> = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    if (match.index! > last) pieces.push(text.slice(last, match.index));
    pieces.push({ stableKey: match[1] });
    last = match.index! + match[0].length;
  }
  if (last < text.length) pieces.push(text.slice(last));
  return pieces;
}

type CatalogAsset = { stableKey?: unknown; name?: unknown };

function catalogAssets(catalog: unknown): Array<CatalogAsset> {
  if (Array.isArray(catalog)) return catalog as Array<CatalogAsset>;
  const assets = (catalog as { assets?: unknown } | null | undefined)?.assets;
  return Array.isArray(assets) ? (assets as Array<CatalogAsset>) : [];
}

/** Each catalog asset's `@Name` mention, by stable key. */
export function assetMentionByKey(catalog: unknown): Map<string, string> {
  const mentions = new Map<string, string>();
  for (const asset of catalogAssets(catalog)) {
    const key = typeof asset?.stableKey === "string" ? asset.stableKey : "";
    const name = typeof asset?.name === "string" ? asset.name.trim() : "";
    if (key && name && !mentions.has(key)) mentions.set(key, `@${name}`);
  }
  return mentions;
}

/**
 * `value` (any JSON) with every token in every string rendered as the
 * `@Name` mention of the asset it names in `catalog` (a tested-asset block or
 * its asset list). A copy; `value` is not changed. This is the one rendering
 * at the edges of the backend: what leaves it reads as it always has.
 */
export function renderAssetRefTokens<T>(value: T, catalog: unknown): T {
  // Nothing to render: the same value, so a payload with no token is the
  // same bytes it was.
  if (!JSON.stringify(value ?? null).includes(OPEN)) return value;
  const mentions = assetMentionByKey(catalog);
  const render = (inner: unknown): unknown => {
    if (typeof inner === "string") {
      return replaceAssetRefTokens(inner, (key) => mentions.get(key));
    }
    if (Array.isArray(inner)) return inner.map(render);
    if (inner && typeof inner === "object") {
      if (inner instanceof Date) return inner;
      return Object.fromEntries(
        Object.entries(inner as Record<string, unknown>).map(([key, child]) => [
          key,
          render(child),
        ]),
      );
    }
    return inner;
  };
  return render(value) as T;
}
